/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  SpecTemplateClauses,
  SpecTemplateSectionIs,
  SpecTemplateTextIs,
} from '@gh/builder-shared-data';
import {
  AbstractSnapshotFormComponent,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
  toAppFormGroup,
  updateChildrenSchema,
  updateSchemaWithChildren,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { sliceDataByQuery } from '@gh/core-data';
import { LocalizationService } from '@gh/core-mls';
import { AutocompleteLoadMoreEvent, getControlValue, setControlValue } from '@gh/core-ui';
import { of$ } from '@gh/rx';
import { assign, find, get } from 'lodash';
import {
  JobSpecClause,
  JobSpecQuoteChange,
  JobSpecSectionInfo,
} from '../../../resources/job.model';

const getJobSpecSectionFormConfig = (paragraphIncluded: boolean, section: JobSpecSectionInfo) => ({
  text: {
    required: paragraphIncluded && section.isSectionIncluded && section.textIs === SpecTemplateTextIs.Mandatory,
    visible: section.textIs === SpecTemplateTextIs.Optional || section.textIs === SpecTemplateTextIs.Mandatory,
    enabled: paragraphIncluded && section.isSectionIncluded && section.textIs !== SpecTemplateTextIs.NotAllowed,
  },
  clauses: void 0,
  isSectionIncluded: { visible: section.sectionIs === SpecTemplateSectionIs.Optional, enabled: paragraphIncluded },
  isTextIncluded: {
    visible: section.textIs === SpecTemplateTextIs.Optional,
    enabled: paragraphIncluded && section.isSectionIncluded,
  },
  isTextWarning: void 0,
  sectionIs: void 0,
  textIs: void 0,
  clausesAre: void 0,
  clause: void 0,
  clauseValue: {
    visible: section.clausesAre === SpecTemplateClauses.Allowed,
    enabled: paragraphIncluded && section.isSectionIncluded,
  },
  name: void 0,
});

@Component({
  standalone: false,
  selector: 'gh-job-spec-section-form',
  templateUrl: 'job-spec-section-form.component.html',
  styleUrls: ['job-spec-section-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobSpecSectionFormComponent extends AbstractSnapshotFormComponent<JobSpecSectionInfo> implements OnInit {

  @Input() sequence: number;
  @Input() paragraphIncluded: boolean;

  @Output() moveQuoteChangeBack = new EventEmitter<JobSpecQuoteChange>();

  constructor(private route: ActivatedRoute,
              private formStateBuilder: FormStateBuilder,
              private localizationService: LocalizationService) {
    super();
  }

  get hasWarningText(): boolean {
    return getControlValue(this.form.group, 'isTextWarning');
  }

  get hasCurrentClauseWarningText(): boolean {
    const clauseValue = getControlValue(this.form.group, 'clauseValue');
    const clauses = getControlValue(this.form.group, 'clauses');
    return get(find(clauses, { text: clauseValue }), 'isTextWarning') || false;
  }

  ngOnInit(): void {
    super.ngOnInit();

    assign(this, getRouteInitData(this.route));
  }

  loadClauses($event: AutocompleteLoadMoreEvent): void {
    const { load, query } = $event;
    const stringHelper = this.localizationService.getStringL10nHelper();

    load(of$(sliceDataByQuery(
      getControlValue(this.form.group, 'clauses')
        .filter((clause: JobSpecClause) => stringHelper.includesIgnoreCase(clause.text, query.filter)),
      query)));
  }

  onClauseSelect(clause?: JobSpecClause): void {
    setControlValue(this.form.group, 'clause', clause);
  }

  onChange(): void {
    updateChildrenSchema(
      toAppFormGroup(this.form.group),
      getJobSpecSectionFormConfig(this.paragraphIncluded, this.form.submitValue));
  }

  protected buildForm(snapshot: FormStateSnapshot<JobSpecSectionInfo>): FormState<JobSpecSectionInfo> {
    return this.formStateBuilder.buildPlain({
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      snapshot,
      children: getJobSpecSectionFormConfig(this.paragraphIncluded, getFormStateSnapshotValue(snapshot)),
    });
  }

  protected afterRestored(current: FormStateSnapshot<JobSpecSectionInfo>): void {
    updateSchemaWithChildren(toAppFormGroup(this.form.group), {
      enabled: whenCreateOrEditMode(),
      children: getJobSpecSectionFormConfig(this.paragraphIncluded, getFormStateSnapshotValue(current)),
    });
  }
}
